import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel } from '@/components/kit';
import { Stars } from '@/components/inputs';
import { BackLink, recordGate } from '@/components/record';
import ReviewDialog from '@/components/account/ReviewDialog';
import { REVIEW_FIELDS } from '@/shared/fields';
import { planName } from '@/shared/pricing';
import { committed, idOf } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/account/reviews/:id', layout: 'owner', access: "profile === 'Customer_c'" };

export default function ReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: review, loading, error, run } = useFetch(
    () => sdk.table('reviews_c').select(REVIEW_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/account/reviews" label="Back to Reviews" />;
  const gate = recordGate({ back, loading, record: review, error, run, what: 'Review' });
  if (gate) return gate;

  const orderId = idOf(review.order_c);

  async function remove() {
    const res = await sdk.table('reviews_c').remove(review.Id);
    if (!committed(res, 'Review deleted')) return false;
    navigate('/account/reviews');
    return true;
  }

  return (
    <div className="mx-auto max-w-4xl">
      {back}
      <PageHeader
        eyebrow={`Posted ${formatLocalDate(review.CreatedOn, DATE_FORMATS.SHORT)}`}
        title={review.Name}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmDelete
              title="Delete this review?"
              description="It comes off the storefront straight away. This cannot be undone."
              onConfirm={remove}
            />
          </>
        }
      />

      {review.hidden_c && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-warning-border bg-warning-muted px-4 py-3 text-sm text-warning">
          <ApperIcon name="EyeOff" size={18} className="mt-0.5 shrink-0" />
          <p>The team has hidden this review from the storefront. Open a support ticket if you think that is a mistake.</p>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="space-y-6">
          <Panel title="Your review" icon="Star" bodyClassName="space-y-3 py-4">
            <Stars value={review.rating_c} size={20} />
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{review.body_c}</p>
            <p className="text-xs text-muted-foreground">— {review.display_name_c || 'Anonymous viewer'}</p>
          </Panel>

          <Panel title="Reply from Ghost Cable" icon="MessageSquareReply" bodyClassName="py-4">
            {review.reply_c ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{review.reply_c}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No reply yet. The team reads every review.</p>
            )}
          </Panel>
        </div>

        <Panel title="Details" icon="Info">
          <dl className="divide-y divide-border">
            <DetailRow label="Rating">{`${review.rating_c} / 5`}</DetailRow>
            <DetailRow label="Plan">{planName(review.plan_c)}</DetailRow>
            <DetailRow label="Order">
              {orderId ? (
                <Link to={`/account/orders/${orderId}`} className="font-mono text-primary hover:underline">
                  {review.order_c?.Name ?? 'View order'}
                </Link>
              ) : null}
            </DetailRow>
            <DetailRow label="Shown as">{review.display_name_c}</DetailRow>
            <DetailRow label="On storefront">{review.hidden_c ? 'Hidden' : 'Visible'}</DetailRow>
          </dl>
        </Panel>
      </div>

      <ReviewDialog open={editOpen} onOpenChange={setEditOpen} review={review} onSaved={run} />
    </div>
  );
}
